import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Heading,
  Text,
  Textarea,
  VStack,
  List,
  ListItem,
  ListIcon,
  Flex,
  SimpleGrid,
  Icon,
  Link,
  useToast
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useRecoilValue, useRecoilState } from 'recoil';
import { userAtom } from '../state/userAtom';
import { siteContentAtom, siteContentLoadedAtom } from '../state/filterAtom';
import { getSiteContent, updateSiteContent } from '../api/media';
import { FaArrowLeft, FaChevronRight } from 'react-icons/fa';

const CAMPAIGN_USES = [
  {
    category: 'Recruitment & Talent',
    items: [
      'Job fairs and career expos',
      'High school and college campus outreach',
      'Open house / facility tours for prospective hires'
    ]
  },
  {
    category: 'Events & Community',
    items: [
      'Conferences and trade shows',
      'Community sponsorship events',
      'Charitable events and volunteer days'
    ]
  },
  {
    category: 'Government & Advocacy',
    items: [
      'Legislative briefings and lobbying visits',
      'Regulatory agency presentations'
    ]
  },
  {
    category: 'Customer & Business Development',
    items: [
      'Customer onboarding and kickoff meetings',
      'Investor and stakeholder presentations',
      'Vendor and supplier meetings'
    ]
  },
  {
    category: 'Digital & Communications',
    items: [
      'Email newsletters and customer communications',
      'Company websites',
      'Social media platforms'
    ]
  },
  {
    category: 'Internal Operations',
    items: [
      'New employee orientation packets',
      'Employee resource boards and breakrooms',
      'Employee communication platforms'
    ]
  },
  {
    category: 'Media & PR',
    items: [
      'Press kits and media inquiries',
      'Local newspaper and TV interviews',
      'Op-eds and guest columns',
      'Crisis communications support',
      'Industry publication features'
    ]
  },
  {
    category: 'Print & Physical Placements',
    items: [
      'Waiting rooms and lobbies',
      'Trade show booths and signage',
      'Trucks and equipment',
      'Branded merchandise and swag'
    ]
  }
];

const EditableHeading = ({ contentKey, value, onSave, isAdmin, as, ...headingProps }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const handleBlur = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) {
      onSave(contentKey, trimmed);
    } else {
      setDraft(value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    }
  };

  if (!isAdmin) {
    return <Heading as={as} {...headingProps}>{value}</Heading>;
  }

  if (editing) {
    return (
      <Textarea
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        autoFocus
        variant="unstyled"
        resize="none"
        rows={1}
        p={1}
        border="1px dashed"
        borderColor="brand.ltRed"
        borderRadius="md"
        {...headingProps}
      />
    );
  }

  return (
    <Heading
      as={as}
      {...headingProps}
      cursor="pointer"
      _hover={{ outline: '1px dashed', outlineColor: 'brand.ltRed', outlineOffset: '4px', borderRadius: 'md' }}
      onClick={() => setEditing(true)}
    >
      {value}
    </Heading>
  );
};

export const CampaignUsesPage = () => {
  const user = useRecoilValue(userAtom);
  const isAdmin = user?.role === 'admin';
  const toast = useToast();
  const [content, setContent] = useRecoilState(siteContentAtom);
  const [contentLoaded, setContentLoaded] = useRecoilState(siteContentLoadedAtom);

  useEffect(() => {
    if (contentLoaded) return;

    let cancelled = false;
    const loadContent = async () => {
      try {
        const data = await getSiteContent();
        if (!cancelled) {
          setContent((prev) => ({ ...prev, ...data }));
          setContentLoaded(true);
        }
      } catch (err) {
        if (!cancelled) console.error('Failed to load site content');
      }
    };
    loadContent();
    return () => { cancelled = true; };
  }, [contentLoaded, setContent, setContentLoaded]);

  const handleSave = useCallback(async (key, value) => {
    try {
      await updateSiteContent(key, value);
      setContent((prev) => ({ ...prev, [key]: value }));
      toast({ title: 'Content saved', status: 'success', duration: 2000 });
    } catch (err) {
      console.error('Failed to save content:', err);
      toast({ title: 'Failed to save content', description: err.response?.data?.error || err.message, status: 'error', duration: 4000 });
    }
  }, [toast, setContent]);

  return (
    <Box>
      <Box
        position="relative"
        color="brand.white"
        minH="400px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        py={{ base: 16, md: 0 }}
        px={{ base: 4, md: 8 }}
        textAlign="center"
        backgroundImage="url('/img/bg-hero.jpg')"
        backgroundSize="cover"
        backgroundPosition="center bottom"
      >
        {contentLoaded && (
        <EditableHeading
          contentKey="campaign_uses_heading"
          value={content.campaign_uses_heading}
          onSave={handleSave}
          isAdmin={isAdmin}
          as="h1"
          fontSize={{ base: '3xl', md: '6xl' }}
          fontWeight="700"
          lineHeight="1.0"
          letterSpacing="-0.02em"
          maxW="800px"
          mx="auto"
          textShadow="2px 2px 8px rgba(0, 0, 0, 0.3)"
          color="brand.white"
        />
        )}
      </Box>

      <Box px={{ base: 4, md: 8 }} py={8} maxW="1400px" mx="auto">
        <Link
          as={RouterLink}
          to="/"
          display="inline-flex"
          alignItems="center"
          gap={2}
          color="brand.dkBlue"
          fontWeight="600"
          fontSize="sm"
          mb={8}
          _hover={{ textDecoration: 'underline' }}
        >
          <Icon as={FaArrowLeft} boxSize={3} />
          Back to Library
        </Link>

        <Flex
          direction={{ base: 'column', lg: 'row' }}
          gap={8}
          align="flex-start"
        >
          <VStack flex={3} align="flex-start" spacing={6}>
            <Heading
              as="h2"
              fontSize={{ base: '2xl', md: '3xl' }}
              fontWeight="700"
              color="brand.dkBlue"
              lineHeight="1.1"
            >
              Potential Uses for This Material
            </Heading>
            <Text fontSize="md" color="brand.dkGray2" lineHeight="1.7">
              The industry messaging campaign assets were intentionally designed for broad application. Each one of us has a use for these materials, whether personally or as part of our formal job roles. The more we use them, the louder our industry's true story gets told.
            </Text>
            <Text fontSize="md" color="brand.dkGray2" lineHeight="1.7">
              This list is a starting point, not a limit. As you find new ways to use the materials, please share with us — your ideas and real-world examples will help shape how this campaign grows.
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="100%" pt={2}>
              {CAMPAIGN_USES.map((section) => (
                <Box key={section.category}>
                  <Heading
                    as="h3"
                    fontSize="lg"
                    fontWeight="700"
                    color="brand.dkBlue"
                    mb={3}
                  >
                    {section.category}
                  </Heading>
                  <List spacing={2} pl={1}>
                    {section.items.map((item) => (
                      <ListItem
                        key={item}
                        fontSize="md"
                        color="brand.dkGray2"
                        display="flex"
                        alignItems="center"
                      >
                        <ListIcon as={FaChevronRight} color="brand.ltRed" boxSize={2.5} />
                        {item}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>

          <Box
            flex={1}
            minW={{ lg: '280px' }}
            maxW={{ lg: '320px' }}
            position={{ lg: 'sticky' }}
            top={{ lg: '100px' }}
          >
            <Box
              bg="brand.dkBlue"
              color="brand.white"
              p={6}
              borderRadius="md"
            >
              <Heading as="h4" fontSize="lg" fontWeight="700" mb={3} lineHeight="1.3">
                Our industry has a story worth telling.
              </Heading>
              <Text fontSize="sm" lineHeight="1.7">
                IADC has launched a reputational messaging campaign to set the record straight: drilling is essential. Much of what the world needs starts with us.
              </Text>
            </Box>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};
